import React, { useState } from 'react';
import type { SeedCampaign } from '../../lib/seedService';

interface BoostPanelProps {
  campaigns: SeedCampaign[];
  label: string;
  onBoost: (campaign: string, count: number, spread: number) => Promise<number>;
  onComplete?: (campaign: string, result: number) => void;
}

const BoostPanel: React.FC<BoostPanelProps> = ({ campaigns, label, onBoost, onComplete }) => {
  const [campaign, setCampaign] = useState('');
  const [count, setCount] = useState(10);
  const [spread, setSpread] = useState(3);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const handleBoost = async () => {
    if (!campaign) return;
    setLoading(true);
    setResult(null);
    try {
      const n = await onBoost(campaign, count, spread);
      setResult(`${n} ok`);
      onComplete?.(campaign, n);
    } catch (err: any) {
      setResult(`Erreur: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-end gap-1.5 bg-gray-50 rounded-lg p-2">
      <div className="min-w-0 flex-1">
        <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Campagne</label>
        <select
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
          className="w-full px-1.5 py-1 border border-gray-200 rounded text-xs bg-white"
        >
          <option value="">Choisir...</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="w-16">
        <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Nb</label>
        <input
          type="number"
          min={1}
          max={200}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="w-full px-1.5 py-1 border border-gray-200 rounded text-xs"
        />
      </div>
      <div className="w-14">
        <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Jours</label>
        <input
          type="number"
          min={1}
          max={30}
          value={spread}
          onChange={(e) => setSpread(Number(e.target.value))}
          className="w-full px-1.5 py-1 border border-gray-200 rounded text-xs"
        />
      </div>
      <button
        onClick={handleBoost}
        disabled={!campaign || loading}
        className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors disabled:opacity-40 whitespace-nowrap"
      >
        {loading ? '...' : label}
      </button>
      {result && (
        <span className={`text-[10px] whitespace-nowrap ${result.startsWith('Erreur') ? 'text-red-500' : 'text-green-600'}`}>
          {result}
        </span>
      )}
    </div>
  );
};

export default BoostPanel;
